/**
 * Spot Normalization (pure function, zero imports)
 *
 * data/spots.json spots are snake_case (noaa_station_id, buoys[].id, ...),
 * but frontend state getters read camelCase (currentSpot.noaaStationId,
 * buoys[0].buoyId — mirroring transformSpot in
 * netlify/functions/get-conditions.mjs), while shared/rating/algorithm.mjs
 * reads the snake_case fields (swell_window_min, ideal_wind, skill_level...).
 *
 * normalizeSpot() bridges the two: it ADDS the camelCase fields the state
 * layer needs while KEEPING every snake_case original on the same object so
 * the rating algorithm keeps working untouched.
 */

/**
 * Normalize a raw spot object from data/spots.json
 * Idempotent: already-normalized spots are returned as-is.
 * @param {Object|null} raw - Raw spot from spots.json (snake_case)
 * @returns {Object|null} Spot with BOTH snake_case and camelCase fields
 */
export function normalizeSpot(raw) {
    if (!raw) return null;
    if (raw._normalized) return raw;

    return {
        // Keep every snake_case original (rating algorithm reads these)
        ...raw,
        _normalized: true,

        // camelCase mirrors of transformSpot() in get-conditions.mjs
        noaaStationId: raw.noaa_station_id ?? raw.noaaStationId ?? null,
        swellWindow: raw.swellWindow ?? {
            min: raw.swell_window_min ?? null,
            max: raw.swell_window_max ?? null
        },
        breakType: raw.break_type ?? raw.breakType ?? null,
        waveType: raw.wave_type ?? raw.waveType ?? null,
        skillLevel: raw.skill_level ?? raw.skillLevel ?? null,
        crowdFactor: raw.crowd_factor ?? raw.crowdFactor ?? null,
        idealConditions: raw.idealConditions ?? {
            swellMin: raw.ideal_swell_min ?? null,
            swellMax: raw.ideal_swell_max ?? null,
            tide: raw.ideal_tide ?? null,
            wind: raw.ideal_wind ?? null
        },
        accessNotes: raw.access_notes ?? raw.accessNotes ?? null,

        // Buoys: spots.json uses { id, source, priority, coefficient };
        // state getters read buoys[0].buoyId. Keep originals, add buoyId.
        buoys: (raw.buoys || []).map(b => ({
            ...b,
            buoyId: b.buoyId ?? b.id ?? b.buoy_id ?? null
        }))
    };
}
